// /client/src/components/EditProgram.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';
import ProgramForm from './ProgramForm';

const EditProgram = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // Get program ID from URL
  
  const [initialData, setInitialData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState('');

  // Fetch program data on component mount
  useEffect(() => {
    const fetchProgram = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No authentication token found');
          return;
        }
        
        const response = await fetch(`/api/programs/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Transform the data to extract productId string if it's an object
          const transformedData = {
            ...data,
            productId: data.productId?._id || data.productId || ''
          };
          
          setInitialData(transformedData);
        } else {
          const errorData = await response.json();
          console.error('API error:', errorData);
          setError(errorData.error || 'Failed to fetch program');
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setError('Failed to load program');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProgram();
    }
  }, [id]);

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      const response = await fetch(`/api/programs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // Success - redirect to programs list with success message
        navigate('/programs', {
          state: { successMessage: 'Program updated successfully!' }
        });
      } else {
        const errorData = await response.json();
        console.error('Failed to update program:', errorData);
        setError(errorData.error || 'Failed to update program');
      }
    } catch (error) {
      console.error('Error updating program:', error);
      setError('An error occurred while updating the program');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found');
        setError('No authentication token found');
        setArchiving(false);
        return;
      }
      
      const response = await fetch(`/api/programs/${id}/archive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        // Success - redirect to programs list with success message
        navigate('/programs', {
          state: { successMessage: 'Program archived successfully!' }
        });
      } else {
        const errorData = await response.json();
        console.error('Failed to archive program:', errorData);
        setError(errorData.error || 'Failed to archive program');
      }
    } catch (error) {
      console.error('Error archiving program:', error);
      setError('An error occurred while archiving the program');
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found');
        setError('No authentication token found');
        setRestoring(false);
        return;
      }
      
      const response = await fetch(`/api/programs/${id}/restore`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        // Success - redirect to programs list with success message
        navigate('/programs', {
          state: { successMessage: 'Program restored successfully!' }
        });
      } else {
        const errorData = await response.json();
        console.error('Failed to restore program:', errorData);
        setError(errorData.error || 'Failed to restore program');
      }
    } catch (error) {
      console.error('Error restoring program:', error);
      setError('An error occurred while restoring the program');
    } finally {
      setRestoring(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="has-text-centered py-6">
                <div className="is-loading"></div>
                <p>Loading program...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Show error state
  if (error && !initialData._id) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => navigate('/programs')}
                ></button>
                {error}
              </div>
              <button 
                className="button" 
                onClick={() => navigate('/programs')}
              >
                Back to Programs
              </button>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Check if program is already archived
  const isArchived = initialData.status === 'archived';

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />
          
          <div className='mt-2 pt-6 container px-6'>
            <button 
              className="button is-small is-text mb-4"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
            <div className='is-flex is-flex-direction-rows is-justify-content-space-between is-align-items-center'>
              <h1 className='h1 my-2'>Edit Program</h1>
              
              {/* Archive Program Button - only show for active programs */}
              {!isArchived && (
                <button
                  className={`button is-warning ${archiving ? 'is-loading' : ''}`}
                  onClick={handleArchive}
                  disabled={archiving || submitting}
                >
                  <span>Archive Program</span>
                </button>
              )}
              
              {/* Restore Program Button - only show for archived programs */}
              {isArchived && (
                <button
                  className={`button is-success ${restoring ? 'is-loading' : ''}`}
                  onClick={handleRestore}
                  disabled={restoring}
                >
                  <span>Restore Program</span>
                </button>
              )}
            </div>
          </div>

          <div className='container px-6'>
            {error && (
              <div className="notification is-danger mb-4">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
              </div>
            )}
            
            {isArchived && (
              <div className="notification is-warning mb-4">
                <p>
                  <strong>This program is archived.</strong> You cannot edit archived programs. 
                  To make changes, please restore the program first.
                </p>
              </div>
            )}
            
            <ProgramForm 
              initialData={initialData}
              onSubmit={handleSubmit}
              submitButtonText="Update Program"
              isLoading={submitting}
              mode="edit"
              disabled={isArchived}
            />
          </div>

        </div>
      </section>
    </>
  );
};

export default EditProgram;